import { tsValidate_OptionalArray, tsValidateArray, tsValidateBoolean, tsValidateOptionalId, tsValidateString, tsValidateUniqueId, tsValidateValue, tsValidator_arrayOfUniqueIds, tsValidator_nonMandatoryString } from '@nu-art/ts-common';
import { criticalityValues, Metadata_Epidemiology, Metadata_Prognosis, Validator_Epidemiology, Validator_Prognosis } from '../../_enum.js';
import { knowledgeManagerDBGroup } from '@app/hcs-shared';
const Validator_ModifiableProps = {
    //Linking fields
    variableId: tsValidateUniqueId,
    referenceIds: tsValidator_arrayOfUniqueIds,
    chiefComplaintIds: tsValidator_arrayOfUniqueIds,
    variantsGroupId: tsValidateOptionalId,
    //Profile Data
    label: tsValidateString(),
    initials: tsValidateString(),
    colorGroup: tsValidateUniqueId,
    profileDescription: tsValidator_nonMandatoryString,
    profileNotes: tsValidator_nonMandatoryString,
    scores: tsValidator_nonMandatoryString,
    icd: tsValidator_arrayOfUniqueIds,
    criticality: tsValidateValue([...criticalityValues], false),
    completed: tsValidateBoolean(false),
    tagIds: tsValidateArray({ key: tsValidateString(-1), value: tsValidateArray(tsValidateString(-1)) }),
    scoreReferences: tsValidate_OptionalArray({
        variableId: tsValidateOptionalId,
        mdCalcUrl: tsValidateString(-1, false)
    }),
    //Epidemiology Data
    epidemiology: tsValidateArray(Validator_Epidemiology, false),
    epidemiologyNotes: tsValidateString(-1, false),
    //Prognosis Data
    prognosis: tsValidateArray(Validator_Prognosis, false),
    prognosisNotes: tsValidateString(-1, false),
    //Optional HTML attached at import / in edit mode (Storage download URL, set only by controlled endpoints)
    diseaseProfileHtmlUrl: tsValidateString(-1, false),
};
const Validator_GeneratedProps = {
    _valueIds: tsValidateArray(tsValidateUniqueId),
    _variableIds: tsValidateArray(tsValidateUniqueId),
    _tagIds: tsValidateArray(tsValidateUniqueId),
};
export const DBDef_DiseaseProfile = {
    modifiablePropsValidator: Validator_ModifiableProps,
    generatedPropsValidator: Validator_GeneratedProps,
    versions: ['1.0.7', '1.0.6', '1.0.5', '1.0.4', '1.0.3'],
    dbKey: 'disease-profiles',
    entityName: 'DiseaseProfile',
    frontend: {
        group: knowledgeManagerDBGroup,
        name: 'disease-profile'
    },
    backend: {
        name: 'disease-profiles'
    },
    dependencies: {
        '_variableIds': {
            dbKey: 'variables',
            fieldType: 'string[]'
        },
        '_valueIds': {
            dbKey: 'values',
            fieldType: 'string[]'
        },
        'referenceIds': {
            dbKey: 'references',
            fieldType: 'string[]'
        },
        'icd': {
            dbKey: 'references',
            fieldType: 'string[]'
        },
        'colorGroup': {
            dbKey: 'color-groups',
            fieldType: 'string'
        },
        '_tagIds': {
            dbKey: 'tags',
            fieldType: 'string[]'
        },
    },
    metadata: {
        variableId: {
            optional: false,
            description: `ID of correlated Disease's Variable`,
            valueType: 'string',
        },
        referenceIds: {
            optional: false,
            description: '',
            valueType: 'string[]',
            metadata: {
                optional: false,
                description: 'IDs of Related Reference', valueType: 'string'
            }
        },
        chiefComplaintIds: {
            optional: false,
            description: `ID of correlated Disease's Variable`,
            valueType: 'string[]',
            metadata: {
                optional: false,
                description: 'IDs of Related Chief Complaints', valueType: 'string'
            }
        },
        variantsGroupId: {
            optional: true,
            description: '',
            valueType: 'string'
        },
        label: {
            optional: false,
            description: '',
            valueType: 'string'
        },
        initials: {
            optional: false,
            description: '',
            valueType: 'string'
        },
        colorGroup: {
            optional: false,
            description: 'An id of the color group representing the system for this disease profile',
            valueType: 'string',
        },
        profileDescription: { optional: true, description: '', valueType: 'string' },
        profileNotes: { optional: true, description: '', valueType: 'string' },
        scores: { optional: true, description: '', valueType: 'string' },
        icd: {
            optional: false,
            description: '',
            valueType: 'string[]',
            metadata: {
                optional: false,
                description: 'IDs of Related ICD Reference', valueType: 'string'
            }
        },
        criticality: {
            optional: true,
            description: '',
            valueType: 'string'
        },
        completed: {
            optional: true,
            description: 'Whether the disease profile is completed or not.',
            valueType: 'boolean'
        },
        tagIds: {
            optional: false,
            description: '',
            valueType: 'object[]',
            metadata: {
                key: {
                    optional: false,
                    description: '',
                    valueType: 'string'
                },
                value: {
                    optional: false,
                    description: '',
                    valueType: 'string[]',
                    metadata: {
                        optional: false,
                        description: '',
                        valueType: 'string'
                    }
                }
            }
        },
        scoreReferences: {
            optional: true,
            description: '',
            valueType: 'object[]',
            metadata: {
                variableId: { optional: true, description: '', valueType: 'string' },
                mdCalcUrl: { optional: true, description: '', valueType: 'string' },
            }
        },
        epidemiology: {
            optional: true,
            description: 'Array of epidemiology data',
            valueType: 'object[]',
            metadata: Metadata_Epidemiology
        },
        epidemiologyNotes: {
            optional: true,
            description: 'Notes on the epidemiology',
            valueType: 'string',
        },
        prognosis: {
            optional: true,
            description: 'Array of prognosis data',
            valueType: 'object[]',
            metadata: Metadata_Prognosis,
        },
        prognosisNotes: {
            optional: true,
            description: 'Notes on the prognosis',
            valueType: 'string',
        },
        diseaseProfileHtmlUrl: {
            optional: true,
            description: 'Firebase Storage download URL (with token) of the HTML optionally attached to this disease profile at CSV import or in edit mode',
            valueType: 'string',
        },
        _variableIds: {
            optional: false,
            description: 'Generated prop, array of all variable dependencies on a disease profile',
            valueType: 'string[]',
            metadata: {
                valueType: 'string',
                optional: false,
                description: 'variable Id'
            }
        },
        _valueIds: {
            optional: false,
            description: 'Generated prop, array of all values dependencies on a disease profile',
            valueType: 'string[]',
            metadata: {
                valueType: 'string',
                optional: false,
                description: 'value Id'
            }
        },
        _tagIds: {
            optional: false,
            description: 'Generated prop, array of all values dependencies on a disease profile',
            valueType: 'string[]',
            metadata: {
                valueType: 'string',
                optional: false,
                description: 'value Id'
            }
        }
    }
};
//# sourceMappingURL=db-def.js.map